import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { can } from "../permissions";
import { includesFolded } from "../utils/text";
import {
  IconCatalog, IconPlus, IconLoader, IconAlert, IconSearch, IconX,
} from "../components/Icons";
import ColorPicker from "../components/ColorPicker";
import SearchableSelect from "../components/SearchableSelect";
import Toggle from "../components/Toggle";
import BranchSelector from "../components/BranchSelector";

// ---- Types ----
export interface Category {
  id: string;
  name: string;
  code: string | null;
  parent_id: string | null;
  color: string | null;
  icon: string | null;
  sort_order: number;
  is_active: boolean;
  order_group: string | null;
  branch_ids: string[];
}
export interface Branch {
  id: string;
  name: string;
  code: string;
}
export interface VatRate {
  id: string;
  code: string;
  name: string;
  rate: number;
  is_default: boolean;
}
export interface Variant {
  id: string;
  name: string;
  price_mode: string;
  price: number;
  sku: string | null;
  is_active: boolean;
}
export interface Product {
  id: string;
  name: string;
  alt_name: string | null;
  code: string | null;
  barcode: string | null;
  barcodes?: string[];
  category_id: string | null;
  vat_rate_id: string | null;
  price: number;
  cost: number | null;
  unit: string;
  description: string | null;
  subtitle: string | null;
  color: string | null;
  image_url: string | null;
  is_sellable: boolean;
  product_type: string;
  deduction_sources: { source_product_id: string; source_product_name: string | null; quantity: number }[];
  has_variants: boolean;
  is_active: boolean;
  exclude_from_revenue: boolean;
  cash_payout: boolean;
  enter_price_at_pos: boolean;
  order_group: string | null;
  sort_order: number;
  variants: Variant[];
  branch_ids: string[];
  warehouse_ids: string[];
  tags: string[];
}
export interface Warehouse {
  id: string;
  name: string;
  code: string;
}
export interface Tag {
  id: string;
  name: string;
  color: string | null;
}

type Tab = "products" | "categories" | "tags";

export const UNIT_LABELS: Record<string, string> = {
  ks: "ks", kg: "kg", g: "g", l: "l", ml: "ml", min: "min", hod: "hod", bal: "bal",
};

function formatPrice(minor: number): string {
  return new Intl.NumberFormat("cs-CZ", { style: "currency", currency: "CZK", minimumFractionDigits: 0 }).format(minor / 100);
}

export default function Catalog({ initialTab = "products" }: { initialTab?: Tab }) {
  const { user } = useCurrentUser();
  const canWrite = can(user, "catalog", "write");
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab] = useState<Tab>(initialTab);
  const [productSection, setProductSection] = useState<"manage" | "bulk">("manage");
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [bulkAction, setBulkAction] = useState<string>("");
  const [bulkValue, setBulkValue] = useState<string>("");
  const [categories, setCategories] = useState<Category[]>([]);
  const [vatRates, setVatRates] = useState<VatRate[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  // Product list filters survive navigation to the product form and back —
  // persisted in sessionStorage (per tab, cleared on tab close).
  const [search, setSearch] = useState(() => sessionStorage.getItem("lpos:catalog:search") ?? "");
  const [filterCat, setFilterCat] = useState<string>(() => sessionStorage.getItem("lpos:catalog:cat") ?? "all");
  useEffect(() => { sessionStorage.setItem("lpos:catalog:search", search); }, [search]);
  useEffect(() => { sessionStorage.setItem("lpos:catalog:cat", filterCat); }, [filterCat]);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showTagForm, setShowTagForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [recipeImportOpen, setRecipeImportOpen] = useState(false);
  const [importResult, setImportResult] = useState<{ total: number; created: number; updated: number; errors: number; messages: string[] } | null>(null);
  const [draggedCategoryId, setDraggedCategoryId] = useState<string | null>(null);
  const [draggedProductId, setDraggedProductId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const recipeFileInputRef = useRef<HTMLInputElement>(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [cats, vats, branchList, warehouseList, prods, tagList] = await Promise.all([
        api.get<Category[]>("/catalog/categories").then(r => r.data),
        api.get<VatRate[]>("/catalog/vat-rates").then(r => r.data),
        api.get<Branch[]>("/branches").then(r => r.data),
        api.get<Warehouse[]>("/warehouses").then(r => r.data),
        api.get<Product[]>("/catalog/products?limit=500").then(r => r.data),
        api.get<Tag[]>("/catalog/tags").then(r => r.data),
      ]);
      setCategories(cats);
      setVatRates(vats);
      setBranches(branchList);
      setWarehouses(warehouseList);
      setProducts(prods);
      setTags(tagList);
    } catch (e) {
      setErr(apiError(e, t("catalog.loadError")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadAll(); }, [loadAll]);

  async function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    if (ext) form.append("format", ext);
    try {
      setLoading(true);
      const { data } = await api.post("/catalog/products/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      await loadAll();
    } catch (e) {
      setErr(apiError(e, t("catalog.importError")));
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function downloadTemplate(format: "csv" | "xlsx") {
    const { data, headers } = await api.get(`/catalog/products/import/template?format=${format}`, {
      responseType: "blob",
    });
    const ct = typeof headers["content-type"] === "string" ? headers["content-type"] : (format === "csv" ? "text/csv" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
    const blob = new Blob([data], { type: ct });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `luigipos_import_sablona.${format}`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  async function handleRecipeImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    if (ext) form.append("format", ext);
    try {
      setLoading(true);
      const { data } = await api.post("/catalog/products/import/recipe", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      await loadAll();
    } catch (e) {
      setErr(apiError(e, t("catalog.recipeImportError")));
    } finally {
      setLoading(false);
      if (recipeFileInputRef.current) recipeFileInputRef.current.value = "";
    }
  }

  async function downloadRecipeTemplate(format: "csv" | "xlsx") {
    const { data, headers } = await api.get(`/catalog/products/import/recipe/template?format=${format}`, {
      responseType: "blob",
    });
    const ct = typeof headers["content-type"] === "string" ? headers["content-type"] : (format === "csv" ? "text/csv" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
    const blob = new Blob([data], { type: ct });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `luigipos_receptury_sablona.${format}`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  const catName = (id: string | null) => categories.find(c => c.id === id)?.name || "—";
  const vatRate = (id: string | null) => vatRates.find(v => v.id === id);

  const filteredProducts = products.filter(p => {
    if (filterCat !== "all" && p.category_id !== filterCat) return false;
    if (search) {
      return includesFolded(p.name, search) || includesFolded(p.code || "", search);
    }
    return true;
  });

  // ---- Product CRUD ----
  async function deleteProduct(id: string) {
    if (!confirm(t("catalog.deleteProductConfirm"))) return;
    try {
      await api.delete(`/catalog/products/${id}`);
      await loadAll();
    } catch (e) {
      setErr(apiError(e, t("catalog.deleteProductError")));
    }
  }

  // ---- Category CRUD ----
  async function saveCategory(data: Record<string, unknown>) {
    setErr(null);
    try {
      if (editingCategory) {
        await api.put(`/catalog/categories/${editingCategory.id}`, data);
      } else {
        await api.post("/catalog/categories", data);
      }
      setShowCategoryForm(false);
      setEditingCategory(null);
      await loadAll();
    } catch (e) {
      setErr(apiError(e, t("catalog.saveCategoryError")));
    }
  }

  async function deleteCategory(id: string) {
    if (!confirm(t("catalog.deleteCategoryConfirm"))) return;
    try {
      await api.delete(`/catalog/categories/${id}`);
      await loadAll();
    } catch (e) {
      setErr(apiError(e, t("catalog.deleteCategoryError")));
    }
  }

  async function handleCategoryDrop(targetId: string) {
    if (!draggedCategoryId || draggedCategoryId === targetId) {
      setDraggedCategoryId(null);
      return;
    }
    const sorted = [...categories].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
    const ids = sorted.map(c => c.id);
    const from = ids.indexOf(draggedCategoryId);
    const to = ids.indexOf(targetId);
    if (from === -1 || to === -1) {
      setDraggedCategoryId(null);
      return;
    }
    ids.splice(from, 1);
    ids.splice(to, 0, draggedCategoryId);
    setDraggedCategoryId(null);
    try {
      await api.post("/catalog/categories/reorder", { ordered_ids: ids });
      await loadAll();
    } catch (e) {
      setErr(apiError(e, t("catalog.reorderError")));
    }
  }

  async function handleProductDrop(targetId: string) {
    if (!draggedProductId || draggedProductId === targetId) {
      setDraggedProductId(null);
      return;
    }
    const ids = filteredProducts.map(p => p.id);
    const from = ids.indexOf(draggedProductId);
    const to = ids.indexOf(targetId);
    if (from === -1 || to === -1) {
      setDraggedProductId(null);
      return;
    }
    ids.splice(from, 1);
    ids.splice(to, 0, draggedProductId);
    setDraggedProductId(null);
    try {
      await api.post("/catalog/products/reorder", {
        ordered_ids: ids,
        category_id: filterCat !== "all" ? filterCat : undefined,
      });
      await loadAll();
    } catch (e) {
      setErr(apiError(e, t("catalog.reorderError")));
    }
  }

  async function sortCategoriesAlphabetically() {
    const ids = [...categories]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(c => c.id);
    try {
      await api.post("/catalog/categories/reorder", { ordered_ids: ids });
      await loadAll();
    } catch (e) {
      setErr(apiError(e, t("catalog.sortError")));
    }
  }

  async function sortProductsAlphabetically() {
    const sorted = [...filteredProducts].sort((a, b) => a.name.localeCompare(b.name));
    const ids = sorted.map(p => p.id);
    try {
      await api.post("/catalog/products/reorder", {
        ordered_ids: ids,
        category_id: filterCat !== "all" ? filterCat : undefined,
      });
      await loadAll();
    } catch (e) {
      setErr(apiError(e, t("catalog.sortError")));
    }
  }

  // ---- Tag CRUD ----
  async function saveTag(name: string, color: string | null) {
    setErr(null);
    try {
      await api.post("/catalog/tags", { name, color: color || null });
      setShowTagForm(false);
      await loadAll();
    } catch (e) {
      setErr(apiError(e, t("catalog.createTagError")));
    }
  }

  async function deleteTag(id: string) {
    if (!confirm(t("catalog.deleteTagConfirm"))) return;
    try {
      await api.delete(`/catalog/tags/${id}`);
      await loadAll();
    } catch (e) {
      setErr(apiError(e, t("catalog.deleteTagError")));
    }
  }

  // ---- Variant CRUD ----
  async function addVariant(productId: string, data: Record<string, unknown>) {
    setErr(null);
    try {
      await api.post(`/catalog/products/${productId}/variants`, data);
      await loadAll();
    } catch (e) {
      setErr(apiError(e, t("catalog.addVariantError")));
    }
  }

  async function deleteVariant(productId: string, variantId: string) {
    try {
      await api.delete(`/catalog/products/${productId}/variants/${variantId}`);
      await loadAll();
    } catch (e) {
      setErr(apiError(e, t("catalog.deleteVariantError")));
    }
  }

  async function bulkApply() {
    if (!selectedProductIds.length) {
      setErr(t("catalog.bulkSelectProducts"));
      return;
    }
    if (!bulkAction) {
      setErr(t("catalog.bulkSelectAction"));
      return;
    }
    if (bulkAction === "delete" && !confirm(t("catalog.bulkDeleteConfirm", { count: selectedProductIds.length }))) return;
    setLoading(true);
    try {
      await api.post("/catalog/products/bulk", {
        product_ids: selectedProductIds,
        action: bulkAction,
        value: bulkValue || null,
      });
      setSelectedProductIds([]);
      setBulkAction("");
      setBulkValue("");
      await loadAll();
    } catch (e) {
      setErr(apiError(e, t("catalog.bulkError")));
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
        <IconLoader size={32} className="muted" />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1>{tab === "products" ? t("catalog.productsTitle") : tab === "categories" ? t("catalog.categoriesTitle") : t("catalog.tagsTitle")}</h1>
          <p>{tab === "products" ? t("catalog.productsSubtitle") : tab === "categories" ? t("catalog.categoriesSubtitle") : t("catalog.tagsSubtitle")}</p>
        </div>
        {tab === "products" && canWrite && (
          <div style={{ display: "flex", gap: 8 }}>
            <button className="primary" onClick={() => navigate("/catalog/products/new")}>
              <IconPlus size={18} /> {t("catalog.newProduct")}
            </button>
            <div style={{ position: "relative" }}>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                ref={fileInputRef}
                onChange={handleImportFile}
                style={{ display: "none" }}
              />
              <div style={{ display: "flex" }}>
                <button
                  className="primary"
                  style={{ borderRadius: "var(--radius-sm) 0 0 var(--radius-sm)", borderRight: "1px solid rgba(255,255,255,0.25)" }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {t("catalog.importProducts")}
                </button>
                <button
                  className="primary"
                  style={{ borderRadius: "0 var(--radius-sm) var(--radius-sm) 0", padding: "0 10px" }}
                  onClick={() => setImportOpen(!importOpen)}
                >
                  <span style={{ fontSize: 10 }}>▼</span>
                </button>
              </div>
              {importOpen && (
                <div
                  style={{
                    position: "absolute", top: "100%", right: 0, marginTop: 4,
                    minWidth: 220, borderRadius: "var(--radius-sm)", border: "1px solid var(--border)",
                    background: "var(--bg-elevated)", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", zIndex: 100,
                  }}
                >
                  <div
                    style={{ padding: "10px 14px", fontSize: 14, cursor: "pointer", borderBottom: "1px solid var(--border)" }}
                    onClick={() => { setImportOpen(false); downloadTemplate("csv"); }}
                  >
                    {t("catalog.downloadProductCsvTemplate")}
                  </div>
                  <div
                    style={{ padding: "10px 14px", fontSize: 14, cursor: "pointer" }}
                    onClick={() => { setImportOpen(false); downloadTemplate("xlsx"); }}
                  >
                    {t("catalog.downloadProductXlsxTemplate")}
                  </div>
                </div>
              )}
            </div>
            <div style={{ position: "relative" }}>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                ref={recipeFileInputRef}
                onChange={handleRecipeImportFile}
                style={{ display: "none" }}
              />
              <div style={{ display: "flex" }}>
                <button
                  className="primary"
                  style={{ borderRadius: "var(--radius-sm) 0 0 var(--radius-sm)", borderRight: "1px solid rgba(255,255,255,0.25)" }}
                  onClick={() => recipeFileInputRef.current?.click()}
                >
                  {t("catalog.importRecipes")}
                </button>
                <button
                  className="primary"
                  style={{ borderRadius: "0 var(--radius-sm) var(--radius-sm) 0", padding: "0 10px" }}
                  onClick={() => setRecipeImportOpen(!recipeImportOpen)}
                >
                  <span style={{ fontSize: 10 }}>▼</span>
                </button>
              </div>
              {recipeImportOpen && (
                <div
                  style={{
                    position: "absolute", top: "100%", right: 0, marginTop: 4,
                    minWidth: 220, borderRadius: "var(--radius-sm)", border: "1px solid var(--border)",
                    background: "var(--bg-elevated)", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", zIndex: 100,
                  }}
                >
                  <div
                    style={{ padding: "10px 14px", fontSize: 14, cursor: "pointer", borderBottom: "1px solid var(--border)" }}
                    onClick={() => { setRecipeImportOpen(false); downloadRecipeTemplate("csv"); }}
                  >
                    {t("catalog.downloadRecipeCsvTemplate")}
                  </div>
                  <div
                    style={{ padding: "10px 14px", fontSize: 14, cursor: "pointer" }}
                    onClick={() => { setRecipeImportOpen(false); downloadRecipeTemplate("xlsx"); }}
                  >
                    {t("catalog.downloadRecipeXlsxTemplate")}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {tab === "categories" && canWrite && (
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="primary"
              onClick={() => {
                if (confirm(t("catalog.sortConfirmCategories"))) {
                  void sortCategoriesAlphabetically();
                }
              }}
            >
              {t("catalog.sortAlphabetically")}
            </button>
            <button className="primary" onClick={() => { setEditingCategory(null); setShowCategoryForm(true); }}>
              <IconPlus size={18} /> {t("catalog.newCategory")}
            </button>
          </div>
        )}
        {tab === "tags" && canWrite && (
          <button className="primary" onClick={() => setShowTagForm(true)}>
            <IconPlus size={18} /> {t("catalog.newTag")}
          </button>
        )}
      </div>

      {err && (
        <div className="card" style={{
          padding: "12px 16px", marginBottom: 20,
          background: "var(--red-dim)", color: "var(--red)",
          display: "flex", alignItems: "center", gap: 8, fontSize: 14,
        }}>
          <IconAlert size={16} /> {err}
        </div>
      )}

      {importResult && (
        <div className="card" style={{ padding: 16, marginBottom: 20, background: "var(--green-dim)", color: "var(--green)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <strong>{t("inventory.importResultTitle")}</strong>
            <button className="ghost" onClick={() => setImportResult(null)} style={{ color: "var(--green)", padding: "4px 8px" }}>×</button>
          </div>
          <p style={{ fontSize: 14, margin: 0 }}>
            {t("inventory.importResultSummary", { total: importResult.total, created: importResult.created, updated: importResult.updated, errors: importResult.errors })}
          </p>
          {importResult.messages.length > 0 && (
            <ul style={{ fontSize: 13, margin: "8px 0 0", paddingLeft: 18 }}>
              {importResult.messages.slice(0, 10).map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          )}
        </div>
      )}

      {/* ---- Products tab ---- */}
      {tab === "products" && (
        <>
          <div style={{ display: "flex", gap: 4, marginBottom: 16, borderBottom: "1px solid var(--border)" }}>
            {[
              { id: "manage", label: t("catalog.tabManage") },
              ...(canWrite ? [{ id: "bulk", label: t("catalog.tabBulk") }] : []),
            ].map(s => (
              <button
                key={s.id}
                onClick={() => { setProductSection(s.id as "manage" | "bulk"); setSelectedProductIds([]); }}
                style={{
                  padding: "8px 16px", border: "none", background: "transparent",
                  color: productSection === s.id ? "var(--accent)" : "var(--muted)",
                  fontWeight: productSection === s.id ? 600 : 500, fontSize: 13,
                  borderBottom: productSection === s.id ? "2px solid var(--accent)" : "2px solid transparent",
                  cursor: "pointer", marginBottom: -1,
                }}
              >
                {s.label}
              </button>
            ))}
          </div>

          {productSection === "bulk" && (
            <div className="card" style={{ padding: 16, marginBottom: 16 }}>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
                <div>
                  <label>{t("common.actions")}</label>
                  <select value={bulkAction} onChange={e => setBulkAction(e.target.value)} style={{ minWidth: 220 }}>
                    <option value="">{t("catalog.bulkSelectActionPh")}</option>
                    <option value="activate">{t("common.activate")}</option>
                    <option value="deactivate">{t("common.deactivate")}</option>
                    <option value="set_category">{t("catalog.bulkSetCategory")}</option>
                    <option value="set_vat_rate">{t("catalog.bulkSetVat")}</option>
                    <option value="set_unit">{t("catalog.bulkSetUnit")}</option>
                    <option value="set_color">{t("catalog.bulkSetColor")}</option>
                    <option value="set_branches">{t("catalog.bulkSetBranches")}</option>
                    <option value="set_warehouses">{t("catalog.bulkSetWarehouses")}</option>
                    <option value="delete">{t("common.delete")}</option>
                  </select>
                </div>
                {["set_category", "set_vat_rate", "set_unit", "set_color"].includes(bulkAction) && (
                  <div>
                    <label>{t("catalog.bulkValue")}</label>
                    {bulkAction === "set_category" && (
                      <SearchableSelect value={bulkValue} onChange={setBulkValue} options={categories.map(c => ({ value: c.id, label: c.name }))} placeholder={t("catalog.selectCategory")} />
                    )}
                    {bulkAction === "set_vat_rate" && (
                      <SearchableSelect value={bulkValue} onChange={setBulkValue} options={vatRates.map(v => ({ value: v.id, label: `${v.name} (${v.rate}%)` }))} placeholder={t("catalog.selectVat")} />
                    )}
                    {bulkAction === "set_unit" && (
                      <SearchableSelect value={bulkValue} onChange={setBulkValue} options={Object.keys(UNIT_LABELS).map(u => ({ value: u, label: u }))} placeholder={t("catalog.selectUnit")} />
                    )}
                    {bulkAction === "set_color" && (
                      <input value={bulkValue} onChange={e => setBulkValue(e.target.value)} placeholder="#ff6b6b" />
                    )}
                  </div>
                )}
                {["set_branches", "set_warehouses"].includes(bulkAction) && (
                  <div style={{ minWidth: 260 }}>
                    <label>{bulkAction === "set_branches" ? t("staff.colBranches") : t("nav.warehouses")}</label>
                    <BranchSelector
                      branches={bulkAction === "set_branches" ? branches : warehouses.map(w => ({ id: w.id, name: `${w.name}${w.code ? ` (${w.code})` : ""}` }))}
                      selected={bulkValue.split(",").filter(Boolean)}
                      onChange={(ids) => setBulkValue(ids.join(","))}
                      label=""
                    />
                  </div>
                )}
                <button className="primary" onClick={bulkApply} disabled={!selectedProductIds.length || !bulkAction}>{t("catalog.bulkApply", { count: selectedProductIds.length })}</button>
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "flex-start" }}>
            <div style={{ position: "relative", flex: "1 1 auto", minWidth: 0, maxWidth: 360 }}>
              <IconSearch size={18} className="muted" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
              <input
                placeholder={t("catalog.searchProduct")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ paddingLeft: 38, width: "100%" }}
              />
            </div>
            <div style={{ width: 240, flexShrink: 0 }}>
              <SearchableSelect
                value={filterCat}
                onChange={(val) => setFilterCat(val)}
                options={[{ value: "all", label: t("catalog.allCategories") }, ...categories.map(c => ({ value: c.id, label: c.name }))]}
                placeholder={t("catalog.allCategories")}
                searchPlaceholder={t("catalog.searchCategory")}
              />
            </div>
            {(search || filterCat !== "all") && (
              <button
                className="ghost"
                onClick={() => { setSearch(""); setFilterCat("all"); }}
                title={t("catalog.clearFilter")}
              >
                <IconX size={16} /> {t("catalog.clearFilter")}
              </button>
            )}
            {canWrite && (
            <button
              className="primary"
              style={{ marginLeft: "auto" }}
              onClick={() => {
                if (confirm(t("catalog.sortConfirm"))) {
                  void sortProductsAlphabetically();
                }
              }}
            >
              {t("catalog.sortAlphabetically")}
            </button>
            )}
          </div>
          {filteredProducts.length === 0 ? (
            <EmptyState icon={<IconCatalog size={48} className="muted" />} title={t("catalog.noProducts")} subtitle={t("catalog.createFirstProduct")} />
          ) : (
            <div className="card">
              <table>
                <thead>
                  <tr>
                    {productSection === "bulk" && <th><input type="checkbox" checked={selectedProductIds.length === filteredProducts.length && filteredProducts.length > 0} onChange={() => { setSelectedProductIds(selectedProductIds.length === filteredProducts.length ? [] : filteredProducts.map(p => p.id)); }} /></th>}
                    <th>{t("common.name")}</th>
                    <th>{t("customers.colCode")}</th>
                    <th>{t("catalog.colCategory")}</th>
                    <th>{t("orders.colPrice")}</th>
                    <th>DPH</th>
                    <th>{t("catalog.colVariants")}</th>
                    <th>{t("common.status")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map(p => (
                    <ProductRow
                      key={p.id}
                      product={p}
                      selected={selectedProductIds.includes(p.id)}
                      onToggleSelect={productSection === "bulk" ? () => setSelectedProductIds(prev => prev.includes(p.id) ? prev.filter(id => id !== p.id) : [...prev, p.id]) : undefined}
                      catName={catName(p.category_id)}
                      vatRateStr={vatRate(p.vat_rate_id) ? `${vatRate(p.vat_rate_id)!.rate}%` : "—"}
                      canWrite={canWrite}
                      onEdit={() => navigate(`/catalog/products/${p.id}/edit`)}
                      onDelete={() => deleteProduct(p.id)}
                      onAddVariant={(data) => addVariant(p.id, data)}
                      onDeleteVariant={(vid) => deleteVariant(p.id, vid)}
                      onDragStart={canWrite ? () => setDraggedProductId(p.id) : undefined}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => handleProductDrop(p.id)}
                      onDragEnd={() => setDraggedProductId(null)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ---- Categories tab ---- */}
      {tab === "categories" && (
        <>
          {canWrite && showCategoryForm && (
            <CategoryForm
              key={editingCategory?.id || "new"}
              category={editingCategory}
              branches={branches}
              orderGroups={[...new Set(categories.map((c) => c.order_group).filter((g): g is string => !!g))]}
              onSave={saveCategory}
              onCancel={() => { setShowCategoryForm(false); setEditingCategory(null); }}
            />
          )}
          {categories.length === 0 ? (
            <EmptyState icon={<IconCatalog size={48} className="muted" />} title={t("catalog.noCategories")} subtitle={t("catalog.createFirstCategory")} />
          ) : (
            <div className="card">
              <table>
                <thead>
                  <tr><th>{t("common.name")}</th><th>{t("customers.colCode")}</th><th>{t("customers.color")}</th><th>{t("common.status")}</th><th></th></tr>
                </thead>
                <tbody>
                  {[...categories].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)).map((c) => (
                    <tr
                      key={c.id}
                      draggable
                      onDragStart={() => setDraggedCategoryId(c.id)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => handleCategoryDrop(c.id)}
                      onDragEnd={() => setDraggedCategoryId(null)}
                      style={{ cursor: canWrite ? "grab" : "default" }}
                    >
                      <td style={{ fontWeight: 600 }}>
                        {c.color && <span style={{ display: "inline-block", width: 12, height: 12, borderRadius: 3, background: c.color, marginRight: 8 }} />}
                        {c.name}
                      </td>
                      <td className="mono">{c.code || "—"}</td>
                      <td>{c.color || "—"}</td>
                      <td><span className={c.is_active ? "badge badge-green" : "badge badge-red"}>{c.is_active ? t("common.active") : t("common.inactive")}</span></td>
                      <td>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {canWrite && (
                          <>
                          <button className="ghost" style={{ padding: "4px 10px", fontSize: 12 }} onClick={() => { setEditingCategory(c); setShowCategoryForm(true); }}>{t("common.edit")}</button>
                          <button className="ghost" style={{ padding: "4px 10px", fontSize: 12, color: "var(--red)" }} onClick={() => deleteCategory(c.id)}>{t("common.delete")}</button>
                          </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ---- Tags tab ---- */}
      {tab === "tags" && (
        <>
          {canWrite && showTagForm && (
            <TagForm onSave={saveTag} onCancel={() => setShowTagForm(false)} />
          )}
          {tags.length === 0 ? (
            <EmptyState icon={<IconCatalog size={48} className="muted" />} title={t("catalog.noTags")} subtitle={t("catalog.createFirstTag")} />
          ) : (
            <div className="card" style={{ padding: 20 }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {tags.map(t => (
                  <div key={t.id} style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "8px 14px", borderRadius: 20,
                    background: t.color || "var(--accent-dim)", color: t.color ? "#fff" : "var(--text)",
                    fontSize: 13, fontWeight: 500,
                  }}>
                    {t.name}
                    {canWrite && (
                    <button onClick={() => deleteTag(t.id)} style={{ border: "none", background: "transparent", cursor: "pointer", color: "inherit", opacity: 0.7, padding: 0, fontSize: 16 }}>×</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---- Sub-components ----
function EmptyState({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle: string }) {
  return (
    <div className="card" style={{ padding: 48, textAlign: "center" }}>
      {icon}
      <h3 style={{ marginTop: 16, fontSize: 18, fontWeight: 600 }}>{title}</h3>
      <p style={{ color: "var(--muted)", fontSize: 14, marginTop: 8 }}>{subtitle}</p>
    </div>
  );
}

function ProductRow({ product, catName, vatRateStr, selected, canWrite, onToggleSelect, onEdit, onDelete, onAddVariant, onDeleteVariant, onDragStart, onDragOver, onDrop, onDragEnd }: {
  product: Product; catName: string; vatRateStr: string; canWrite?: boolean;
  selected?: boolean; onToggleSelect?: () => void;
  onEdit: () => void; onDelete: () => void;
  onAddVariant: (data: Record<string, unknown>) => void;
  onDeleteVariant: (vid: string) => void;
  onDragStart?: (e: React.DragEvent<HTMLTableRowElement>) => void;
  onDragOver?: (e: React.DragEvent<HTMLTableRowElement>) => void;
  onDrop?: (e: React.DragEvent<HTMLTableRowElement>) => void;
  onDragEnd?: (e: React.DragEvent<HTMLTableRowElement>) => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [showVarForm, setShowVarForm] = useState(false);
  const [varName, setVarName] = useState("");
  const [varPrice, setVarPrice] = useState("");
  const [varMode, setVarMode] = useState("abs");

  return (
    <>
      <tr
        draggable={canWrite}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragEnd={onDragEnd}
        style={{ cursor: canWrite ? "grab" : "default" }}
      >
        {onToggleSelect !== undefined && (
          <td style={{ width: 32 }}>
            <input
              type="checkbox"
              checked={selected}
              onChange={onToggleSelect}
              style={{ cursor: "pointer" }}
            />
          </td>
        )}
        <td style={{ fontWeight: 600 }}>
          {product.has_variants && (
            <button onClick={() => setExpanded(!expanded)} style={{ border: "none", background: "transparent", cursor: "pointer", marginRight: 6, fontSize: 12, color: "var(--accent)" }}>
              {expanded ? "▼" : "▶"}
            </button>
          )}
          {product.image_url && (
            <img
              src={product.image_url}
              alt=""
              style={{ width: 28, height: 28, objectFit: "contain", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", background: "var(--bg)", marginRight: 8, verticalAlign: "middle" }}
            />
          )}
          {product.name}
        </td>
        <td className="mono">{product.code || "—"}</td>
        <td>{catName}</td>
        <td style={{ fontWeight: 600 }}>{formatPrice(product.price)}</td>
        <td>{vatRateStr}</td>
        <td>{product.variants.length > 0 ? `${product.variants.length}` : "—"}</td>
        <td><span className={product.is_active ? "badge badge-green" : "badge badge-red"}>{product.is_active ? t("common.active") : t("common.inactive")}</span></td>
        <td>
          {canWrite && (
          <div style={{ display: "flex", gap: 8 }}>
            <button className="ghost" style={{ padding: "4px 10px", fontSize: 12 }} onClick={onEdit}>{t("common.edit")}</button>
            <button className="ghost" style={{ padding: "4px 10px", fontSize: 12, color: "var(--red)" }} onClick={onDelete}>{t("common.delete")}</button>
          </div>
          )}
        </td>
      </tr>
      {expanded && (
        <>
          {product.variants.map(v => (
            <tr key={v.id} style={{ background: "var(--bg)" }}>
              {onToggleSelect && <td />}
              <td style={{ paddingLeft: 40, fontSize: 13 }}>↳ {v.name}</td>
              <td className="mono" style={{ fontSize: 13 }}>{v.sku || "—"}</td>
              <td colSpan={2} style={{ fontSize: 13 }}>
                {v.price_mode === "delta" ? (v.price >= 0 ? "+" : "") + formatPrice(v.price) : formatPrice(v.price)}
                <span style={{ color: "var(--muted)", marginLeft: 6 }}>({v.price_mode === "delta" ? t("catalog.priceDelta") : t("catalog.priceAbs")})</span>
              </td>
              <td colSpan={2} style={{ fontSize: 13 }}>{UNIT_LABELS[product.unit] || product.unit}</td>
              <td>
                {canWrite && (
                <button className="ghost" style={{ padding: "4px 10px", fontSize: 12, color: "var(--red)" }} onClick={() => onDeleteVariant(v.id)}>{t("common.delete")}</button>
                )}
              </td>
            </tr>
          ))}
          <tr style={{ background: "var(--bg)" }}>
            {onToggleSelect && <td />}
            <td colSpan={onToggleSelect ? 8 : 7} style={{ paddingLeft: 40 }}>
              {!canWrite ? null : showVarForm ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input placeholder={t("catalog.variantNamePh")} value={varName} onChange={e => setVarName(e.target.value)} style={{ width: 200, fontSize: 13 }} />
                  <input placeholder={t("catalog.variantPricePh")} type="number" value={varPrice} onChange={e => setVarPrice(e.target.value)} style={{ width: 100, fontSize: 13 }} />
                  <SearchableSelect
                    value={varMode}
                    onChange={setVarMode}
                    options={[
                      { value: "abs", label: t("catalog.priceAbs") },
                      { value: "delta", label: t("catalog.priceDelta") },
                    ]}
                    searchPlaceholder={t("catalog.priceModePh")}
                  />
                  <button className="primary" style={{ fontSize: 12, padding: "6px 12px" }} onClick={() => {
                    onAddVariant({ name: varName, price: Math.round(parseFloat(varPrice || "0") * 100), price_mode: varMode });
                    setVarName(""); setVarPrice(""); setVarMode("abs"); setShowVarForm(false);
                  }}>{t("catalog.addVariant")}</button>
                  <button className="ghost" style={{ fontSize: 12, padding: "6px 12px" }} onClick={() => setShowVarForm(false)}>{t("common.cancel")}</button>
                </div>
              ) : (
                <button className="ghost" style={{ fontSize: 12, padding: "6px 12px" }} onClick={() => setShowVarForm(true)}>
                  <IconPlus size={14} /> {t("catalog.addVariantBtn")}
                </button>
              )}
            </td>
          </tr>
        </>
      )}
    </>
  );
}

function CategoryForm({ category, branches, orderGroups, onSave, onCancel }: {
  category: Category | null;
  branches: Branch[];
  orderGroups: string[];
  onSave: (data: Record<string, unknown>) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(category?.name || "");
  const [code, setCode] = useState(category?.code || "");
  const [color, setColor] = useState<string | null>(category?.color || null);
  const [sortOrder, setSortOrder] = useState(String(category?.sort_order || 0));
  const [isActive, setIsActive] = useState(category?.is_active ?? true);
  const [orderGroup, setOrderGroup] = useState(category?.order_group || "");
  const [branchIds, setBranchIds] = useState<string[]>(category?.branch_ids || []);
  const defaultedBranchIds = useRef(false);

  useEffect(() => {
    if (!category && branches.length > 0 && branchIds.length === 0 && !defaultedBranchIds.current) {
      setBranchIds(branches.map(b => b.id));
      defaultedBranchIds.current = true;
    }
  }, [branches, category, branchIds.length]);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    onSave({ name, code: code || null, color: color || null, sort_order: parseInt(sortOrder || "0"), is_active: isActive, order_group: orderGroup.trim() || null, branch_ids: branchIds });
  }

  return (
    <div className="card" style={{ padding: 24, marginBottom: 24 }}>
      <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 20 }}>{category ? t("catalog.editCategory") : t("catalog.newCategory")}</h3>
      <form onSubmit={submit} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
        <div><label>{t("common.name")} *</label><input value={name} onChange={e => setName(e.target.value)} required /></div>
        <div><label>{t("customers.colCode")}</label><input value={code} onChange={e => setCode(e.target.value)} /></div>
        <div><label>{t("customers.color")}</label><ColorPicker value={color} onChange={setColor} placeholder="#ff6b6b" /></div>
        <div><label>{t("catalog.colSort")}</label><input type="number" value={sortOrder} onChange={e => setSortOrder(e.target.value)} /></div>
        <div>
          <label>{t("catalog.orderGroup")}</label>
          <input
            list="order-group-options"
            value={orderGroup}
            onChange={e => setOrderGroup(e.target.value)}
            placeholder={t("catalog.orderGroupPh")}
            maxLength={32}
          />
          <datalist id="order-group-options">
            {orderGroups.map((g) => <option key={g} value={g} />)}
          </datalist>
          <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>{t("catalog.orderGroupHint")}</div>
        </div>
        <div style={{ display: "flex", alignItems: "flex-end" }}>
          <Toggle checked={isActive} onChange={setIsActive} label={t("common.active")} />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <BranchSelector branches={branches} selected={branchIds} onChange={setBranchIds} label={t("catalog.salesLocations")} />
        </div>
        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 12 }}>
          <button type="submit" className="primary">{category ? t("customers.saveChanges") : t("catalog.createCategory")}</button>
          <button type="button" className="ghost" onClick={onCancel}>{t("common.cancel")}</button>
        </div>
      </form>
    </div>
  );
}

function TagForm({ onSave, onCancel }: { onSave: (name: string, color: string | null) => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [color, setColor] = useState("");

  function submit(e: React.FormEvent) {
    e.preventDefault();
    onSave(name, color || null);
  }

  return (
    <div className="card" style={{ padding: 24, marginBottom: 24, maxWidth: 400 }}>
      <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 20 }}>{t("catalog.newTag")}</h3>
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div><label>{t("common.name")} *</label><input value={name} onChange={e => setName(e.target.value)} required /></div>
        <div><label>{t("catalog.colorHex")}</label><input value={color} onChange={e => setColor(e.target.value)} placeholder="#e94560" /></div>
        <div style={{ display: "flex", gap: 12 }}>
          <button type="submit" className="primary">{t("common.create")}</button>
          <button type="button" className="ghost" onClick={onCancel}>{t("common.cancel")}</button>
        </div>
      </form>
    </div>
  );
}
